import {
  BooleanField,
  ContentRelationshipField,
  GroupField,
  ImageField,
  KeyTextField,
  PrismicDocument,
  RichTextField,
  SelectField,
  SliceZone,
} from "@prismicio/client/src/types/value/document";

export enum AppRoutes {
  home = "/",
  login = "/login",
  signup = "/signup",
  welcome = "/welcome",
  setPassword = "/set-password",
  code = "/code",
  capture = "/capture",
  orb = "/orb",
  logout = "/logout",
  loginOrSignup = "/login-or-signup",
  collectionDetails = "/collection-details",
  batchUpload = "/batch-upload",
  irisUpload = "/iris-upload",
  deletionRequest = "/deletion-request",
  pcrCapture = "/pcr-capture",
  taskInstructions = "/task-instructions",
  stepCapture = "/step-capture",
  stepOrb = "/step-orb",
  stepRtms = "/step-rtms",
  rtmsSequence = "/rtms-sequence",
  multiStepUpload = "/multi-step-upload",
  videoUpload = "/video-upload",
}

export const ALL_ROUTES = Object.values(AppRoutes);

export const PUBLIC_ROUTES = [
  AppRoutes.login,
  AppRoutes.loginOrSignup,
  AppRoutes.logout,
  AppRoutes.signup,
  AppRoutes.deletionRequest,
  AppRoutes.setPassword,
];

export const PRIVATE_ROUTES = ALL_ROUTES.filter(
  (route) => !PUBLIC_ROUTES.includes(route)
);

export enum SignupScreens {
  Email,
  Information,
  EmailConfirmation,
  EmailVerification,
  PasswordSetup,
}

export const MIN_FILE_SIZE = 3 * 1024; // 3KB
export const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB
export const MAX_VIDEO_SIZE = 250 * 1024 * 1024; // 250MB

export type Contributor = string;

export const CAPTURE_TYPES = [
  "photo",
  "iris",
  "pcr",
  "file",
  "orb",
  "rtms",
  "video",
] as const;

export type CaptureType = (typeof CAPTURE_TYPES)[number];

export type QrCaptureType = "orb" | "rtms";

export function isQrCaptureType(value: unknown): value is QrCaptureType {
  return value === "orb" || value === "rtms";
}

export function isCaptureType(value: unknown): value is CaptureType {
  return (
    typeof value === "string" &&
    (CAPTURE_TYPES as readonly string[]).includes(value)
  );
}

export type PrismicCollectionDetails = PrismicDocument<{
  enable_task_qr_navigation?: BooleanField;
  session_details?: GroupField<PrismicCollectionDetailField>;
  distributor_id?: KeyTextField;
  collection_title: any;
  collection_icon: ImageField;
  collection_intro: any;
  collection_image: ImageField;
  tasks: GroupField<{
    enabled: BooleanField;
    task_name: KeyTextField;
    task_id: KeyTextField;
    capture_type: SelectField;
    submissions_per_session: KeyTextField;
    work_task: ContentRelationshipField<
      "work_task",
      string,
      Pick<PrismicWorkTask["data"], "work_task_icon">
    >;
  }>;
  devices: GroupField<{
    key: KeyTextField;
    label: KeyTextField;
    camera_not_flipped: BooleanField;
  }>;

  task_ids: GroupField<{ key: KeyTextField; value: KeyTextField }>;
}>;

export type PrismicTaskInstructions = PrismicDocument<{
  show_task_intro?: BooleanField;
  task_id: KeyTextField;
  task_name: KeyTextField;
  instruction_content: SliceZone;
}>;

export type PrismicLabel = PrismicDocument<{
  label_id: KeyTextField;
  dropdown_options: KeyTextField;
}>;

export interface PrismicLabelRelationField {
  base_label_id: KeyTextField;
  proposed_label_id: KeyTextField;
  value_mapping: KeyTextField;
}

export interface LabelRelation {
  baseLabelId: string;
  baseValue: string;
  proposedLabelId: string;
  proposedKey: string;
}

export interface PrismicCollectionDetailField {
  [key: string]: any;
  label_id: KeyTextField;
  label_title: KeyTextField;
  label_type: SelectField;
  is_required: BooleanField;
  dropdown_options: KeyTextField;
  label_document: ContentRelationshipField<"label">;
  related_to: KeyTextField;
  _linked_label?: boolean;
  _related_label_id?: string;
  use_as_step_labels?: BooleanField;
  qr_prefill?: BooleanField;
  reuse_previous_value?: BooleanField;
}

export type PrismicWorkTask = PrismicDocument<{
  work_task_icon?: ImageField;
  rtms_execution_mode?: SelectField;
  rtms_sequences?: SliceZone;
  show_task_intro?: BooleanField;
  work_task_id: KeyTextField;
  work_task_name: KeyTextField;
  instructions: RichTextField;
  collection_details: GroupField<PrismicCollectionDetailField>;
  label_relations: SliceZone;
  capture_steps: GroupField<{
    capture_entry_id?: KeyTextField;
    rtms_sequence_id?: KeyTextField;
    context_entry?: SelectField;
    capture_task_id: KeyTextField;
    capture_type: SelectField;
  }>;
}>;
